import { FC, ReactNode, useMemo } from "react";
import { Form } from "react-bootstrap";

import styles from "./pagination.module.scss";

export interface PaginationProps {
  limit: number;
  offset: number;
  totalItems: number;
  onPageChange: (offset: number) => void;
  onPageSizeChange?: (limit: number) => void;
  pageSizes?: number[];
}

interface PaginationItemProps {
  page: number | "Previous" | "Next";
  active?: boolean;
  onClick: () => void;
  disabled?: boolean;
}

const PaginationItem: FC<PaginationItemProps> = ({ onClick, page, active = false, disabled = false }) => {
  if (active) {
    return (
      <li className="page-item active">
        <span className="page-link">{page}</span>
      </li>
    );
  } else if (disabled) {
    return (
      <li className="page-item disabled">
        <span className="page-link">{page}</span>
      </li>
    );
  } else {
    return (
      <li className="page-item">
        <span className="page-link" role="button" onClick={onClick}>
          {page}
        </span>
      </li>
    );
  }
};

const getPages = (currentPage: number, pageCount: number): (number | "...")[] => {
  if (pageCount <= 9) {
    return [...Array(pageCount).keys()].map((i) => i + 1);
  }

  const delta = 2;
  const left = currentPage - delta;
  const right = currentPage + delta + 1;
  const range: number[] = [];
  const rangeWithDots: (number | "...")[] = [];

  let l: number | null = null;

  for (let i = 1; i <= pageCount; i++) {
    if (i == 1 || i == pageCount || (i >= left && i < right)) {
      range.push(i);
    }
  }

  for (const i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push("...");
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
};

export const Pagination: FC<PaginationProps> = ({
  limit,
  offset,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizes = [10, 20, 50],
}) => {
  const pagination = useMemo(() => {
    const pageCount = totalItems > 0 ? Math.ceil(totalItems / limit) : 1;
    const currentPage = offset / limit + 1;

    const pageItems: ReactNode[] = [];
    pageItems.push(
      <PaginationItem
        key={`pagination-item-previous`}
        disabled={currentPage === 1}
        onClick={() => onPageChange(offset - limit)}
        page="Previous"
      />
    );

    pageItems.push(
      ...getPages(currentPage, pageCount).map((p, index) => {
        if (typeof p === "string") {
          return (
            <li className="page-item" key={`pagination-item-${index}`}>
              <span className="page-link">...</span>
            </li>
          );
        } else {
          return (
            <PaginationItem
              key={`pagination-item-${index}`}
              onClick={() => onPageChange((p - 1) * limit)}
              page={p}
              active={offset === (p - 1) * limit}
            />
          );
        }
      })
    );

    pageItems.push(
      <PaginationItem
        key={`pagination-item-next`}
        disabled={currentPage === pageCount}
        onClick={() => onPageChange(offset + limit)}
        page="Next"
      />
    );

    return <ul className="pagination justify-content-center">{pageItems}</ul>;
  }, [offset, limit, totalItems, onPageChange]);

  if (onPageSizeChange === undefined) {
    return pagination;
  }

  return (
    <div className={styles.wrapper}>
      {pagination}
      <Form.Select
        className={styles.pageSizeDropdown}
        value={limit}
        onChange={(event) => onPageSizeChange(parseInt(event.target.value))}
      >
        {pageSizes.map((ps, index) => (
          <option key={`page-size-option-${index}`}>{ps}</option>
        ))}
      </Form.Select>
    </div>
  );
};
